import type { MatchState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import type { Vec2, EntityId } from '../types';

// Foul detection (§7.2) — pure. The referee sees an incident if it is in the sight cone, unoccluded, and
// close enough (clarity plateaus to 1.0 within CLARITY_PLATEAU m), OR a linesman flags it (severity-scaled
// base rate). No RNG here — refereeStep does the single detection roll against pDetect.

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Squared distance from point `p` to segment ab. */
function distToSeg(p: Vec2, a: Vec2, b: Vec2): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const l2 = abx * abx + aby * aby;
  const t = l2 > 1e-9 ? clamp01(((p.x - a.x) * abx + (p.y - a.y) * aby) / l2) : 0;
  const cx = a.x + t * abx;
  const cy = a.y + t * aby;
  return Math.hypot(p.x - cx, p.y - cy);
}

/**
 * Referee clarity of the incident at `spot` (§7.2): 0 if out of cone or occluded, else 1 within the plateau
 * distance, tapering to 0 at CONE_DIST. `participants` (offender, victim, diver+marker, the ref) never
 * occlude — the sightline is shortened OCCLUSION_SHORTEN m at the incident end to avoid self-occlusion.
 */
export function refClarity(refPos: Vec2, refFacing: number, spot: Vec2, state: MatchState, participants: ReadonlySet<EntityId>, cfg: SimConfig): number {
  const r = cfg.referee;
  const dx = spot.x - refPos.x;
  const dy = spot.y - refPos.y;
  const dist = Math.hypot(dx, dy);
  if (dist > r.CONE_DIST || dist < 1e-6) return dist < 1e-6 ? 1 : 0;
  const ang = Math.abs(angleDiff(Math.atan2(dy, dx), refFacing));
  if (ang > r.CONE_HALF) return 0;

  // sightline shortened at the incident end so participants clustered on the spot don't self-occlude
  const shorten = Math.min(r.OCCLUSION_SHORTEN, dist);
  const end = { x: spot.x - (dx / dist) * shorten, y: spot.y - (dy / dist) * shorten };
  const capR = cfg.collision.CAPSULE_R;
  for (const p of state.players) {
    if (p.sentOff || participants.has(p.id)) continue;
    if (distToSeg(p.pos, refPos, end) < capR) return 0; // occluded
  }
  return clamp01(1 - Math.max(0, dist - r.CLARITY_PLATEAU) / r.CONE_DIST);
}

/** Detection probability from clarity + severity (§7.2): max of the ref branch and the linesman backstop. */
export function pDetect(clarity: number, severity: number, cfg: SimConfig): number {
  const r = cfg.referee;
  const pLinesman = r.LINESMAN_BASE * (1 + r.K_SEV * severity);
  return clamp01(Math.max(clarity * r.K_REF, pLinesman));
}

function angleDiff(a: number, b: number): number {
  let d = (a - b) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  else if (d < -Math.PI) d += Math.PI * 2;
  return d;
}
